/**
 * Dialog utility for Office Add-ins
 * Provides methods to show, manage and close dialogs
 */
// Dialog event types
export var DialogEventType;
(function (DialogEventType) {
    DialogEventType["DialogMessageReceived"] = "DialogMessageReceived";
    DialogEventType["DialogEventReceived"] = "DialogEventReceived";
})(DialogEventType || (DialogEventType = {}));
/**
 * Dialog service class
 */
export class DialogService {
    constructor() {
        this.dialogObject = null;
        this.eventHandlers = new Map();
        this.isDialogOpen = false;
        this.lastUrl = '';
        /**
         * Close the dialog
         */
        this.CloseDialog = () => {
            if (this.dialogObject) {
                try {
                    this.dialogObject.close();
                }
                catch (error) {
                    console.error('Error closing dialog:', error);
                }
                finally {
                    this.dialogObject = null;
                    this.isDialogOpen = false;
                    this.eventHandlers.clear();
                }
            }
        };
    }
    /**
     * Check if a dialog is currently open
     */
    get hasOpenDialog() {
        return this.isDialogOpen && this.dialogObject !== null;
    }
    /**
     * Show a dialog
     * @param url URL to open in dialog
     * @param options Dialog options
     * @param callback Callback that receives the dialog model
     */
    ShowDialog(url, options, callback) {
        // Check if dialog is already open
        if (this.hasOpenDialog) {
            console.warn('Another dialog is already open. Closing it before opening a new one.');
            this.CloseDialog();
        }
        // Cache the URL for potential reuse
        this.lastUrl = url;
        // Add cache-busting parameter to prevent caching issues
        if (!url.includes('t=')) {
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}t=${Date.now()}`;
        }
        // Setting up dialog options with defaults for better performance
        let dialogOptions = {
            displayInIframe: (options === null || options === void 0 ? void 0 : options.DisplayInIframe) !== undefined ? options.DisplayInIframe : true,
            width: (options === null || options === void 0 ? void 0 : options.Width) || 60,
            height: (options === null || options === void 0 ? void 0 : options.Height) || 40,
            promptBeforeOpen: false
        };
        try {
            // Display the dialog box using displayDialogAsync method
            Office.context.ui.displayDialogAsync(url, dialogOptions, (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                    this.isDialogOpen = true;
                    // If the dialog box is successfully displayed, create a dialog model object
                    const dialogObject = asyncResult.value;
                    this.dialogObject = asyncResult.value;
                    // Create a handler reference map for this dialog
                    this.eventHandlers.clear();
                    const dialogModel = {
                        // Method to add an event handler to the dialog box
                        addEventHandler: (eventType, handler) => {
                            var _a;
                            const officeEventType = eventType === DialogEventType.DialogEventReceived ? Office.EventType.DialogEventReceived : Office.EventType.DialogMessageReceived;
                            // Store reference to handler for cleanup
                            if (!this.eventHandlers.has(officeEventType)) {
                                this.eventHandlers.set(officeEventType, []);
                            }
                            (_a = this.eventHandlers.get(officeEventType)) === null || _a === void 0 ? void 0 : _a.push(handler);
                            dialogObject.addEventHandler(officeEventType, handler);
                        },
                        // Method to close the dialog box
                        close: () => {
                            try {
                                dialogObject.close();
                                this.isDialogOpen = false;
                                this.dialogObject = null;
                                this.eventHandlers.clear();
                            }
                            catch (error) {
                                console.error('Error closing dialog:', error);
                            }
                        },
                        // Method to send a message to the child window of the dialog box
                        messageChild: (message, messageOptions) => {
                            try {
                                if (!messageOptions) {
                                    dialogObject.messageChild(message);
                                }
                                else {
                                    dialogObject.messageChild(message, messageOptions);
                                }
                            }
                            catch (error) {
                                console.error('Error sending message to dialog:', error);
                            }
                        },
                    };
                    // Call the callback function with the dialog model object
                    if (callback) {
                        try {
                            callback(dialogModel);
                        }
                        catch (error) {
                            console.error('Error in dialog callback:', error);
                        }
                    }
                }
                else {
                    this.isDialogOpen = false;
                    // If there's an error, log it to the console
                    console.error("Failed to open dialog:", asyncResult.error);
                }
            });
        }
        catch (error) {
            console.error("Error opening dialog:", error);
            this.isDialogOpen = false;
        }
    }
    /**
     * Reopen the last dialog that was shown
     * @param options New options to use (optional)
     * @param callback Callback that receives the dialog model
     */
    ReopenLastDialog(options, callback) {
        if (this.lastUrl) {
            this.ShowDialog(this.lastUrl, options, callback);
        }
        else {
            console.warn('No previous dialog URL to reopen');
        }
    }
}
// Export singleton instance
export const dialogService = new DialogService();
